import { Link } from "react-router-dom";
import { ArrowRight, Play, BarChart3, Cog, Users, Truck, Container, Wrench } from "lucide-react";

const NAV = ["Solutions", "Industries", "Pricing", "Resources"];

const MODULES = [
  { icon: Truck, title: "SALTWATER HAULING", sub: "DISPATCH  |  TRACK  |  DELIVER" },
  { icon: Container, title: "FRAC TANK RENTALS", sub: "RESERVE  |  DEPLOY  |  MANAGE" },
  { icon: Wrench, title: "FLEET REPAIRS", sub: "SCHEDULE  |  TRACK  |  STAY ON THE ROAD" },
];

const PROOFS = [
  { icon: BarChart3, label: "MORE\nVISIBILITY" },
  { icon: Cog, label: "LESS\nMANUAL WORK" },
  { icon: Users, label: "BUILT FOR\nOILFIELD OPERATORS" },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FAFAF8] text-slate-50" data-testid="landing-page">
      <header className="border-b border-slate-800 bg-white/95 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-8">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <img src="/logo.png?v=5" alt="Well Fleet Logic" className="w-10 h-10 object-contain" />
            <span>
              <span className="block font-heading text-2xl font-bold tracking-wide leading-none text-slate-50">WELL FLEET <span className="text-amber-500">LOGIC</span></span>
              <span className="block text-[10px] font-mono uppercase tracking-[0.35em] text-amber-500">Modular ERP</span>
            </span>
          </Link>
          <nav className="hidden lg:flex items-center gap-8 ml-8">
            {NAV.map((n) => (
              <span key={n} className="text-xs font-mono uppercase tracking-widest text-slate-300 hover:text-amber-500 transition-colors cursor-pointer">
                {n} {["Solutions", "Industries", "Resources"].includes(n) && "▾"}
              </span>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <Link to="/login" data-testid="landing-signin">
              <button className="px-6 py-2.5 border border-slate-300 text-xs font-mono uppercase tracking-widest text-slate-100 hover:border-amber-500 hover:text-amber-500 transition-colors duration-200 bg-white">
                Sign In
              </button>
            </Link>
            <Link to="/login" data-testid="landing-demo-top">
              <button className="px-6 py-2.5 bg-amber-500 text-white text-xs font-mono uppercase tracking-widest hover:bg-amber-400 transition-colors duration-200 shadow-glow">
                Request Demo
              </button>
            </Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-10 grid lg:grid-cols-2 gap-12 items-center relative z-10">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.35em] text-amber-500 mb-6" data-testid="landing-eyebrow">
              Multi-Tenant&nbsp;&nbsp;·&nbsp;&nbsp;Modular&nbsp;&nbsp;·&nbsp;&nbsp;Field-Proven
            </p>
            <h1 className="font-heading text-6xl xl:text-7xl font-bold leading-[0.95] text-slate-50" data-testid="landing-headline">
              One platform for every<br />
              <span className="text-amber-500">barrel, tank &amp; truck.</span>
            </h1>
            <p className="mt-6 text-base text-slate-400 max-w-md leading-relaxed">
              SWD dispatch, frac tank rentals, fleet maintenance and shared customer &amp; asset ledgers — enabled per tenant, billed per module.
            </p>
            <div className="mt-8 flex items-center gap-4">
              <Link to="/login" data-testid="landing-demo-hero">
                <button className="px-7 py-3.5 bg-amber-500 text-white text-sm font-mono uppercase tracking-widest hover:bg-amber-400 transition-colors duration-200 shadow-glow flex items-center gap-2">
                  Request Demo <ArrowRight size={16} />
                </button>
              </Link>
              <Link to="/login" data-testid="landing-how-it-works">
                <button className="px-7 py-3.5 border border-slate-300 text-sm font-mono uppercase tracking-widest text-slate-100 hover:border-amber-500 hover:text-amber-500 transition-colors duration-200 flex items-center gap-2 bg-white">
                  <Play size={16} /> See How It Works
                </button>
              </Link>
            </div>
          </div>
          <div className="relative">
            <p className="hidden xl:block absolute -right-2 top-2 text-[10px] font-mono uppercase tracking-[0.3em] text-slate-300 [writing-mode:vertical-rl]">
              Real Operations. Measurable Results.
            </p>
            <img
              src="/landing-hero.png"
              alt="WellFleetLogic operations — frac tanks, pump trucks and the WellFleetLogic dashboard"
              className="w-full rounded-lg border border-slate-800 shadow-glow-strong object-cover"
              data-testid="landing-hero-image"
            />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 pb-14">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-slate-400 border-l-2 border-amber-500 pl-3 mb-8">
            Keep your operations flowing
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl">
            {PROOFS.map((p) => (
              <div key={p.label} className="flex items-start gap-3 border-l border-slate-800 pl-4">
                <p.icon size={28} className="text-amber-500 flex-shrink-0" strokeWidth={1.5} />
                <p className="text-xs font-mono uppercase tracking-widest text-slate-100 whitespace-pre-line leading-relaxed">{p.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-slate-800 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-10 grid md:grid-cols-4 gap-6 items-stretch">
          {MODULES.map((m) => (
            <div key={m.title} className="flex items-center gap-4 border-r border-slate-800 last:border-0 pr-4">
              <m.icon size={34} className="text-amber-500 flex-shrink-0" strokeWidth={1.5} />
              <div>
                <p className="font-heading text-lg font-bold tracking-wide text-slate-50">{m.title}</p>
                <p className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mt-0.5">{m.sub}</p>
              </div>
            </div>
          ))}
          <div className="bg-amber-500 text-white px-6 py-4 flex items-center -my-10 md:my-0 md:-mr-6">
            <p className="font-heading text-xl font-bold tracking-wide leading-tight">
              ONE PLATFORM.<br />REAL PROGRESS.
            </p>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">© 2026 WellFleetLogic · Modular ERP</p>
          <Link to="/login" className="text-[10px] font-mono uppercase tracking-widest text-slate-400 hover:text-amber-500 transition-colors" data-testid="landing-footer-signin">
            Sign In
          </Link>
        </div>
      </footer>
    </div>
  );
}
